<script lang="ts" setup>
import type { Coordinates } from '@/api';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import TurnstileModal from '@/components/TurnstileModal.vue';
import { useSubscription, type SubscriptionContextStatus } from '@/views/search/useSubscription.ts';

const props = defineProps<{
  location: Coordinates | null;
  distance: number;
  categoryIds: number[];
  contextStatus?: SubscriptionContextStatus;
}>();

const { t } = useI18n();
const contextStatus = computed(() => props.contextStatus ?? 'ready');

const { email, isLoading, isSuccess, errorMessage, isModalOpen, handleSubmit, handleTokenReceived, handleModalClose } = useSubscription(
  () => ({
    location: props.location,
    distance: props.distance,
    categoryIds: props.categoryIds,
  }),
  () => contextStatus.value
);
</script>

<template>
  <section class="border-ph-green-200 bg-ph-green-50 rounded-lg border p-6">
    <div v-if="!isSuccess" class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
      <div class="max-w-xl">
        <h3 class="text-ph-green-800 text-xl font-semibold tracking-wide">{{ t('components.subscribe.banner-title') }}</h3>
      </div>

      <div class="w-full lg:max-w-md">
        <form :aria-busy="isLoading || contextStatus === 'resolving'" class="flex flex-col gap-2 sm:flex-row" @submit.prevent="handleSubmit">
          <label for="subscribe-banner-email" class="sr-only">{{ t('components.subscribe.email') }}</label>
          <input
            id="subscribe-banner-email"
            v-model="email"
            :placeholder="t('components.subscribe.email-placeholder')"
            class="focus:outline-ph-green-500 block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline-2 -outline-offset-1 outline-gray-900/25 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-1 sm:text-sm/6"
            required
            type="email"
          />

          <button
            :disabled="!email || isLoading || contextStatus !== 'ready'"
            class="bg-ph-green-600 hover:bg-ph-green-700 focus:outline-ph-green-500 shrink-0 cursor-pointer rounded-md px-6 py-2 text-sm font-semibold text-white shadow-sm outline-2 outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            type="submit"
          >
            <span v-if="isLoading || contextStatus === 'resolving'">...</span>
            <span v-else>{{ t('components.subscribe.button') }}</span>
          </button>
        </form>

        <div v-if="contextStatus === 'error'" class="mt-2 rounded-md bg-red-50 px-3 py-2 text-red-700">
          <p class="text-xs/4">{{ t('components.subscribe.location-error') }}</p>
        </div>
        <p v-else-if="!errorMessage" class="mt-2 text-xs tracking-tight text-gray-500">
          <span>{{ t('components.subscribe.privacy-consent') }}</span>
          <RouterLink :to="{ name: 'privacy' }" class="text-ph-green-700 hover:text-ph-green-800 underline">
            {{ t('components.subscribe.privacy-policy') }}
          </RouterLink>
          <span>.</span>
        </p>
        <div v-else class="mt-2 rounded-md bg-red-50 px-3 py-2 text-red-700">
          <p class="text-xs/4">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col items-center gap-1 py-2 text-center">
      <p class="text-ph-green-800 text-lg font-semibold">{{ t('components.subscribe.success') }}</p>
      <p class="text-sm text-gray-600">{{ t('components.subscribe.email-sent') }}</p>
    </div>
  </section>

  <TurnstileModal :is-open="isModalOpen" @close="handleModalClose" @token-received="handleTokenReceived" />
</template>

<style lang="css" scoped></style>
